import * as React from "react";
import {
  InvoiceCreatorState,
  InvoiceCreatorStateStatus,
  InvoiceData,
  InvoiceStatus,
} from "../fortnox/invoice_creator_client";
import styled from "styled-components";
import classNames from "classnames";
import InvoiceTable from "./InvoiceTable";
import { areActionsForbidden, CompanyTypeIcon } from ".";

interface Props {
  invoices: InvoiceData[];
  status: InvoiceCreatorStateStatus;
  selectedInvoices: InvoiceData[];
  setSelectedInvoices: (invoices: InvoiceData[]) => void;
  confirmExportInvoices: (invoiceIds: number[]) => void;
}

const Wrapper = styled.div`
  display: grid;
  grid-template-columns: 300px 1fr;
  gap: 0.5rem;

  & > .column {
    display: flex;
    flex-direction: column;
    padding: 0;
    padding-right: 0.5rem;

    &.sidebar {
      contain: size;
      height: 0;
      min-height: 100%;
    }

    & > .content {
      flex: 1;
      overflow-y: auto;
      max-height: 403px; // cursed way to limit the height of the sidebar. value is 500 - height of the rest of the stuff in sidebar
    }
  }
`;

const InvoiceList = ({
  invoices,
  selectedInvoices,
  setSelectedInvoices,
  confirmExportInvoices,
  status,
}: Props) => {
  return (
    <Wrapper>
      <Sidebar
        invoices={invoices}
        onInvoicesSelected={setSelectedInvoices}
        selectedInvoices={selectedInvoices}
        status={status}
        confirmExportInvoices={confirmExportInvoices}
      />
      <InvoiceTable
        invoice={selectedInvoices[selectedInvoices.length - 1]}
        confirmExportInvoices={confirmExportInvoices}
      />
    </Wrapper>
  );
};

const SidebarWrapper = styled.div.attrs({ className: "column sidebar" })`
  border-right: 1px solid rgba(0, 0, 0, 0.1);

  & .search {
    margin-bottom: 0.5rem;

    & input {
      margin-bottom: 0;
    }
  }

  ul {
    list-style-type: none;
    margin: 0;
    padding: 0;
    user-select: none;

    & > li {
      display: flex;
      justify-content: space-between;
      align-items: center;
      padding: 0.25rem 0.5rem;
      cursor: pointer;

      &:hover,
      &:active {
        background-color: rgba(0, 0, 0, 0.1);
      }

      &.active {
        background-color: rgba(0, 0, 0, 0.15);
      }

      &.last-selected {
        outline: 1px dotted black;
      }

      & .status {
        &.sent {
          color: green;
        }

        &.draft {
          color: gray;
        }

        &.error {
          color: red;
        }

        &.warning {
          color: orange;
        }
      }
    }
  }

  .buttons {
    margin-top: 0.5rem;
    width: 100%;

    & input,
    button {
      margin: 0;
      width: 100%;
    }
  }
`;

interface SidebarProps {
  invoices: InvoiceData[];
  onInvoicesSelected: (invoice: InvoiceData[]) => void;
  confirmExportInvoices: (invoiceIds: number[]) => void;
  selectedInvoices: InvoiceData[];
  status: InvoiceCreatorStateStatus;
}

const Sidebar = ({
  invoices,
  onInvoicesSelected,
  confirmExportInvoices,
  selectedInvoices,
  status,
}: SidebarProps) => {
  const [searchText, setSearchText] = React.useState("");
  const filteredInvoices = React.useMemo(() => {
    return invoices.filter((invoice) => {
      const searchTextLower = searchText.toLowerCase();

      return (
        invoice.customerName.toLowerCase().includes(searchTextLower) ||
        invoice.customerNo.includes(searchTextLower)
      );
    });
  }, [invoices, searchText]);
  const [lastSelectedInvoice, setLastSelectedInvoice] =
    React.useState<InvoiceData | null>(null);
  const rowElements = React.useRef<(HTMLElement | null)[]>([]);

  // Update the selected invoice when the filtered invoices change
  React.useEffect(() => {
    const newSelectedInvoices = filteredInvoices.filter((invoice) => {
      return selectedInvoices.includes(invoice);
    });

    // If no invoices are found in the new filter, select the first one
    if (newSelectedInvoices.length == 0 && filteredInvoices.length > 0) {
      newSelectedInvoices.push(filteredInvoices[0]);
    }

    onInvoicesSelected(newSelectedInvoices);
  }, [filteredInvoices]);

  // Update rowElements when the filtered invoices change
  React.useEffect(() => {
    rowElements.current = [];
    rowElements.current = rowElements.current.slice(0, filteredInvoices.length);
  }, [filteredInvoices]);

  function getInvoiceStatusDescription(
    status: InvoiceStatus,
  ): string | undefined {
    switch (status) {
      case InvoiceStatus.Draft:
        return "Förhandsvisning";
      case InvoiceStatus.Sent:
        return "Fortnox faktura skapad";
      case InvoiceStatus.Error:
        return "Ett fel uppstod under skapandet av fakturan";
    }
  }

  const onExportAllClicked = () => {
    const toSend =
      selectedInvoices.length > 1 ? selectedInvoices : filteredInvoices;

    confirmExportInvoices(toSend.map((i) => i.id));
  };

  const onInvoiceSelected = (ev: React.MouseEvent, invoice: InvoiceData) => {
    if (!ev.shiftKey) {
      setLastSelectedInvoice(invoice);
    }

    let newInvoices: InvoiceData[];

    if (ev.ctrlKey) {
      if (selectedInvoices.includes(invoice)) {
        newInvoices = selectedInvoices.filter((i) => i != invoice);
      } else {
        newInvoices = [...selectedInvoices, invoice];
      }
    } else if (ev.shiftKey) {
      const start = Math.min(
        filteredInvoices.indexOf(lastSelectedInvoice || selectedInvoices[0]),
        filteredInvoices.indexOf(invoice),
      );
      const end = Math.max(
        filteredInvoices.indexOf(lastSelectedInvoice || selectedInvoices[0]),
        filteredInvoices.indexOf(invoice),
      );

      newInvoices = filteredInvoices.slice(start, end + 1);
    } else {
      newInvoices = [invoice];
    }

    onInvoicesSelected(newInvoices);
  };

  const onKeyDownOnSidebar = (ev: React.KeyboardEvent) => {
    // Dont't override ctrl+r (browser refresh)
    if (ev.ctrlKey && ev.code == "KeyR") {
      return;
    }

    if (ev.ctrlKey) {
      ev.preventDefault();
      ev.stopPropagation();

      if (ev.code == "KeyA") {
        onInvoicesSelected(filteredInvoices);
        setLastSelectedInvoice(filteredInvoices[0]);
      }

      if (ev.code == "Space" && lastSelectedInvoice != null) {
        // Select or deselect the invoice
        if (selectedInvoices.includes(lastSelectedInvoice)) {
          onInvoicesSelected(
            selectedInvoices.filter((i) => i != lastSelectedInvoice),
          );
        } else {
          onInvoicesSelected([...selectedInvoices, lastSelectedInvoice]);
        }
      }
    }

    if (ev.key == "ArrowUp" || ev.key == "ArrowDown") {
      ev.preventDefault();
      ev.stopPropagation();
      const direction = ev.key == "ArrowUp" ? -1 : 1;

      let newSelectedInvoice;
      let newIndex = -1;

      const index =
        lastSelectedInvoice == null
          ? -1
          : filteredInvoices.indexOf(lastSelectedInvoice);

      if (index >= 0) {
        newIndex = index + direction;

        if (newIndex >= 0 && newIndex < filteredInvoices.length) {
          newSelectedInvoice = filteredInvoices[newIndex];
        }
      }

      if (newSelectedInvoice != null) {
        setLastSelectedInvoice(newSelectedInvoice);

        // scroll into view
        const element = rowElements.current[newIndex];

        if (element != null) {
          element.scrollIntoView({
            block: "nearest",
            behavior: "smooth",
            inline: "nearest",
          });
        }

        if (!ev.ctrlKey) {
          onInvoicesSelected([newSelectedInvoice]);
        }
      }
    }
  };

  return (
    <SidebarWrapper>
      <>
        <div className="search">
          <input
            type="text"
            placeholder="Sök"
            value={searchText}
            disabled={areActionsForbidden(status)}
            onChange={(e) => setSearchText(e.target.value)}
          />
        </div>
        <div className="content">
          <ul onKeyDown={onKeyDownOnSidebar}>
            {filteredInvoices.map((invoice, index) => (
              <li
                key={`${invoice.customerNo}-${invoice.companyType}`}
                ref={(el) => (rowElements.current[index] = el)}
                onClick={(ev) => onInvoiceSelected(ev, invoice)}
                tabIndex={0}
                onKeyDown={(ev) => {
                  if (ev.ctrlKey && ev.code == "KeyR") return; // Dont't override ctrl+r (browser refresh)
                  ev.preventDefault();
                }}
                className={classNames(
                  selectedInvoices.includes(invoice) && "active",
                  lastSelectedInvoice == invoice && "last-selected",
                )}
              >
                <span className="name">
                  <CompanyTypeIcon companyType={invoice.companyType} />
                  {invoice.customerNo} - {invoice.customerName}
                </span>
                <i
                  className={classNames(
                    "status",
                    invoice.status == InvoiceStatus.Draft && [
                      "draft",
                      "fa fa-eye",
                    ],
                    invoice.status == InvoiceStatus.Sent && [
                      "sent",
                      "fa fa-check",
                    ],
                    invoice.status == InvoiceStatus.Error && [
                      "error",
                      "fa fa-triangle-exclamation",
                    ],
                    invoice.status == InvoiceStatus.Sent &&
                      invoice.createTaxReductionError != null && [
                        "warning",
                        "fa fa-triangle-exclamation",
                      ],
                  )}
                  title={getInvoiceStatusDescription(invoice.status)}
                ></i>
              </li>
            ))}
          </ul>
        </div>
        <div className="buttons">
          <button
            className="button primary"
            disabled={areActionsForbidden(status)}
            onClick={onExportAllClicked}
          >
            <i className="fa fa-paper-plane"></i>
            {selectedInvoices.length > 1
              ? "Skicka valda fakturor till Fortnox"
              : "Skicka alla till Fortnox"}
          </button>
        </div>
      </>
    </SidebarWrapper>
  );
};

export default InvoiceList;
